import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const { refreshUser } = useApp();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let count = 0;
    const poll = async () => {
      if (cancelled || count > 20) { if (!cancelled) setStatus("timeout"); return; }
      count += 1;
      setAttempts(count);
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#F9F8F6] grain-bg flex items-center justify-center px-6">
      <div className="card-surface p-12 max-w-lg w-full text-center" data-testid="payment-success">
        {status === "polling" && (
          <>
            <Loader2 size={48} className="text-[#EAB308] mx-auto mb-4 animate-spin" />
            <h1 className="text-3xl font-black text-[#17301C] mb-2">Verifica pagamento...</h1>
            <p className="text-[#6E7A71]">Attendere ({attempts}/20)</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={56} className="text-[#166534] mx-auto mb-4" />
            <h1 className="text-3xl font-black text-[#17301C] mb-2">Benvenuto nel Pro!</h1>
            <p className="text-[#3A4A3E] mb-6">Ora hai accesso a Dashboard, AI Chat illimitata e salvataggio calcoli.</p>
            <Link to="/dashboard" className="btn-primary" data-testid="go-dashboard">
              Vai alla Dashboard <ArrowRight size={16} />
            </Link>
          </>
        )}
        {(status === "failed" || status === "timeout" || status === "error") && (
          <>
            <XCircle size={56} className="text-[#991B1B] mx-auto mb-4" />
            <h1 className="text-3xl font-black text-[#17301C] mb-2">Verifica in corso</h1>
            <p className="text-[#3A4A3E] mb-6">Se hai completato il pagamento, il tuo account sarà aggiornato a breve. Ricarica la pagina o riprova.</p>
            <Link to="/pricing" className="btn-primary">Torna al piano</Link>
          </>
        )}
      </div>
    </div>
  );
};

export const PaymentCancel = () => (
  <div className="min-h-[calc(100vh-72px)] bg-[#F9F8F6] grain-bg flex items-center justify-center px-6">
    <div className="card-surface p-12 max-w-lg w-full text-center" data-testid="payment-cancel">
      <XCircle size={48} className="text-[#6E7A71] mx-auto mb-4" />
      <h1 className="text-3xl font-black text-[#17301C] mb-2">Pagamento annullato</h1>
      <p className="text-[#3A4A3E] mb-6">Nessun addebito effettuato. Puoi riprovare quando vuoi.</p>
      <Link to="/pricing" className="btn-primary" data-testid="retry-pricing">Torna al piano</Link>
    </div>
  </div>
);

export const PayPalReturn = () => {
  const [params] = useSearchParams();
  const subId = params.get("subscription_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const { refreshUser } = useApp();

  useEffect(() => {
    if (!subId) { setStatus("error"); return; }
    let cancelled = false;
    let count = 0;
    const poll = async () => {
      if (cancelled || count > 20) { if (!cancelled) setStatus("timeout"); return; }
      count += 1;
      setAttempts(count);
      try {
        const { data } = await api.get(`/paypal/status/${subId}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setStatus("paid");
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { cancelled = true; };
  }, [subId, refreshUser]);

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#F9F8F6] grain-bg flex items-center justify-center px-6">
      <div className="card-surface p-12 max-w-lg w-full text-center" data-testid="paypal-return">
        {status === "polling" && (
          <>
            <Loader2 size={48} className="text-[#EAB308] mx-auto mb-4 animate-spin" />
            <h1 className="text-3xl font-black text-[#17301C] mb-2">Attivazione PayPal...</h1>
            <p className="text-[#6E7A71]">Attendere ({attempts}/20)</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={56} className="text-[#166534] mx-auto mb-4" />
            <h1 className="text-3xl font-black text-[#17301C] mb-2">Abbonamento PayPal attivo!</h1>
            <Link to="/dashboard" className="btn-primary" data-testid="pp-go-dashboard">Vai alla Dashboard <ArrowRight size={16} /></Link>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <XCircle size={56} className="text-[#991B1B] mx-auto mb-4" />
            <h1 className="text-3xl font-black text-[#17301C] mb-2">Attivazione in corso</h1>
            <p className="text-[#3A4A3E] mb-6">Ricontrolla tra qualche minuto o ricarica la pagina.</p>
            <Link to="/pricing" className="btn-primary">Torna al piano</Link>
          </>
        )}
      </div>
    </div>
  );
};
